import { mapValues } from 'lodash-es';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import type { PsfShareCopyBanditType } from '@change-corgi/middle-end-gql-schema';

import { NotFoundError } from 'src/shared/error';

import type { PsfShareDataQuery, PsfShareDataQueryVariables } from './api.graphql';
import { PsfShareData } from './api.graphql';
import { normalizeDesktopShareAssetsExperiment } from './normalize/normalizeDesktopShareAssetsExperiment';
import type { ShareFeedItem } from './shareFeed';
import { getShareFeed } from './shareFeed';
import { prefetchShareGoal } from './shareGoal';

type Petition = NonNullable<PsfShareDataQuery['petition']>;
type Bandits = Petition['bandits'];
type FeatureConfigs = PsfShareDataQuery['featureConfigs'];
export type PsfSharePageData = {
	petition: Omit<Petition, 'bandits' | 'tagsConnection'> & { categoryTags: string[] };
	bandits: {
		[key in keyof Omit<Bandits, 'id'>]: Omit<Bandits[key], 'id' | 'variants'> & {
			overridden: boolean;
		};
	};
	shareFeed: readonly ShareFeedItem[];
	/**
	 * skip is when coming from the promote_or_share page
	 *
	 * message is when having shared once from the share page
	 */
	type: PsfShareCopyBanditType;
	/**
	 * false when having shared once from the share page
	 */
	initial: boolean;
	/**
	 * true when coming from the promote_or_share page
	 */
	skip: boolean;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimate: number;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimateLocalized: string;
	/**
	 * true when the page is in the sunrise experience
	 */
	isSunriseEnabled: boolean;
	/**
	 * true when the variant is tiktok
	 */
	isTiktokChannelEnabled: boolean;
	desktopShareAssetsExperiment: {
		variation: PsfShareDataQuery['experiments']['desktopShareAssetsExperiment']['variation'];
	};
	/**
	 * Feature configuration flags
	 */
	featureConfigs: FeatureConfigs;
	/**
	 * Pre-fetched sharing goal for the current viewer on this petition.
	 * null when the viewer has no saved goal, is not identified, or the fetch failed.
	 */
	initialShareGoal: number | null;
};
export type PetitionHeadlineBandit = Petition['headlineBandits']['copylinkHeadline'];

/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

function normalizeBandits(petitionBandits: Omit<Petition['bandits'], 'id'>) {
	return mapValues(petitionBandits, ({ variants, ...variant }) => ({
		...variant,
		...(variants?.overrideVariant ? { variant: variants.overrideVariant, overridden: true } : { overridden: false }),
	}));
}

async function normalizeExperiments(
	utilityContext: UtilityContext,
	viewer: PsfShareDataQuery['viewer'],
	experiments: PsfShareDataQuery['experiments'],
	hasShareVisual: boolean,
) {
	return {
		isSunriseEnabled: viewer.experiments.sunriseSupportersV1.variation === 'enabled',
		isTiktokChannelEnabled: experiments.supporterExperiencePsfTiktokShareButton.variation === 'tiktok',
		desktopShareAssetsExperiment: {
			variation: await normalizeDesktopShareAssetsExperiment({
				experiment: experiments.desktopShareAssetsExperiment,
				utilityContext,
				hasShareVisual,
			}),
		},
	};
}

export async function getPsfSharePageData(
	utilityContext: UtilityContext,
	slug: string,
	{
		queryParams,
		banditOverrides,
	}: {
		queryParams: { skip?: string; share?: string };
		banditOverrides: { copy?: string };
	},
): Promise<PsfSharePageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const skip = !!queryParams.skip;
	const initial = !queryParams.share;
	const type = (() => {
		if (skip) return 'SKIP';
		return initial ? 'INITIAL' : 'MESSAGE';
	})();

	const [{ data }, shareFeed] = await Promise.all([
		fetch<PsfShareDataQuery, PsfShareDataQueryVariables>({
			query: PsfShareData,
			variables: {
				slugOrId: slug,
				type,
				includeBanditCopyOverride: !!banditOverrides.copy,
				banditCopyOverrideVariantName: banditOverrides.copy || '',
			},
			rejectOnError: true,
		}),
		getShareFeed(utilityContext, slug),
	]);

	if (!data.petition) {
		throw new NotFoundError();
	}

	const [normalized, initialShareGoal] = await Promise.all([
		normalizeData(utilityContext, {
			skip,
			initial,
			type,
			petition: data.petition,
			shareFeed,
			viewer: data.viewer,
			experiments: data.experiments,
			featureConfigs: data.featureConfigs,
		}),
		prefetchShareGoal(utilityContext, data.petition.id),
	]);

	return { ...normalized, initialShareGoal };
}

async function normalizeData(
	utilityContext: UtilityContext,
	{
		skip,
		initial,
		type,
		petition,
		shareFeed,
		viewer,
		experiments,
		featureConfigs,
	}: {
		skip: boolean;
		initial: boolean;
		type: PsfShareCopyBanditType;
		petition: Petition;
		shareFeed: readonly ShareFeedItem[];
		viewer: PsfShareDataQuery['viewer'];
		experiments: PsfShareDataQuery['experiments'];
		featureConfigs: FeatureConfigs;
	},
): Promise<Omit<PsfSharePageData, 'initialShareGoal'>> {
	const { i18n } = utilityContext;
	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);

	const {
		bandits: { id, ...petitionBandits },
		tagsConnection,
		...rest
	} = petition;

	const bandits = normalizeBandits(petitionBandits);
	const experimentFlags = await normalizeExperiments(
		utilityContext,
		viewer,
		experiments,
		!!petition.bestShareVisual?.url,
	);

	return {
		petition: {
			...rest,
			categoryTags: tagsConnection?.nodes.map((tag) => tag.name) ?? [],
		},
		bandits,
		shareFeed,
		skip,
		initial,
		type,
		signaturesFromShareCountEstimate,
		signaturesFromShareCountEstimateLocalized: i18n.localizeNumber(signaturesFromShareCountEstimate),
		...experimentFlags,
		featureConfigs,
	};
}
